import Component from '../../js/core/component.js';
import generateRandomId from '../../js/utilities/random-id/u-random-id.js';
import keycodes from '../../js/utilities/keycodes/u-keycodes.js';

// Import a polyfill for nodeList.forEach, eg. document.querySelectorAll('.foo').forEach()
import '../../js/vendor/polyfills/polyfill-nodelist-foreach.js';

// Import a polyfill for ChildNode.remove()
import '../../js/vendor/polyfills/polyfill-remove.js';

/**
 * Tabs Class
 */
class Tabs extends Component {

	/**
	 * Default settings for this component
	 * @return {object} Default settings
	 */
	_defaultSettings () {
		return {
			element: null, // The element that contains the elements that will be turned into tabpanels
			hideTitle: true, // By default the element containing the title of each tabpanel is hidden
			enabledTabsClass: 'c-tabs--enabled', // A class name added to enabled tabs components
			tabpanelSelector: '.c-tabs__tabpanel', // Selector for a tabpanel
			tabpanelTitleSelector: '.c-tabs__tabpanel-title', // Selector for the heading inside a tabpanel
			activeTabpanelSelector: '.c-tabs__tabpanel--is-displayed', // Selector for the initially displayed tabpanel. If no tabpanel matches this, the first tabpanel will be displayed.
			tabListClass: 'c-tabs__tablist', // A class name added to the tablist
			tabListItemClass: 'c-tabs__tablist-item', // A class name added to each tablist item
			tabListTriggerClass: 'c-tabs__tablist-trigger' // A class name added to the triggering element in each tablist item
		};
	}

	/**
	 * Check if we should actually create the tabs
	 */
	_prerequisitesMet () {
		// This element already has tabs
		if ((this.element.matches || this.element.msMatchesSelector).call(this.element, '.' + this.settings.enabledTabsClass)) {
			return false;
		}

		// Find and store the tabpanel elements
		this.tabpanelElements = this.element.querySelectorAll(this.settings.tabpanelSelector);

		// This element has no tabpanels
		if (!this.tabpanelElements) {
			return false;
		}

		// Check that each panel has a title
		this.tabpanelElements.forEach(tabpanel => {
			if (!tabpanel.querySelector(this.settings.tabpanelTitleSelector)) {
				return false;
			}
		});

		return true;
	}

	/**
	 * Initialize Tabs
	 */
	_init () {
		this.element = this.settings.element;
		if (this._prerequisitesMet()) {

			// Find and store the link list if there is one
			this.linkList = this.element.querySelector('.' + this.settings.tabListClass);

			// All good–go ahead and create the tabs
			this._create();
		}
	}

	/**
	 * Modify the DOM and set up events
	 */
	_create () {
		let hash = document.location.hash.replace('#', '');
		let tabPanelToDisplay;

		this.tabpanels = [];

		this.tablist = document.createElement('ul');
		this.tablist.classList.add(this.settings.tabListClass);
		this.tablist.setAttribute('role', 'tablist');

		this.tabpanelElements.forEach((tabpanelElement, index) => {
			generateRandomId(tabpanelElement, false, 'tabpanel-');

			// Create an item object
			let tabpanel = {
				tabpanelElement: tabpanelElement,
				id: tabpanelElement.getAttribute('id')
			};

			// Should this be the default selected item?
			if ((hash.length && tabpanel.id === hash) || (!hash.length && (tabpanel.tabpanelElement.matches || tabpanel.tabpanelElement.msMatchesSelector).call(tabpanel.tabpanelElement, this.settings.activeTabpanelSelector)) || index === 0) {
				tabPanelToDisplay = tabpanel;
			}

			// Create a tab that is associated to this tabpanel unless one already exists
			tabpanel.tab = document.createElement('li');
			tabpanel.tab.setAttribute('class', this.settings.tabListItemClass);
			tabpanel.tab.setAttribute('role', 'presentation');

			// Create the element that will display this panel when activated
			tabpanel.trigger = document.createElement('button');
			tabpanel.trigger.setAttribute('type', 'button');
			tabpanel.trigger.setAttribute('class', this.settings.tabListTriggerClass);
			tabpanel.trigger.setAttribute('role', 'tab');
			tabpanel.trigger.setAttribute('aria-controls', tabpanel.id);
			tabpanel.trigger.setAttribute('tabindex', '-1');
			generateRandomId(tabpanel.trigger, true, 'tabpanel-trigger-');

			if (this.linkList) {
				tabpanel.trigger.innerHTML = this.linkList.querySelector('a[href="#' + tabpanel.id + '"]').innerHTML;
			}
			else {
				tabpanel.trigger.innerHTML = tabpanel.tabpanelElement.querySelector(this.settings.tabpanelTitleSelector).innerHTML;
			}

			// Switch tabpanels when a tab is clicked
			tabpanel.trigger.addEventListener('click', this._tabClickEvent.bind(this, tabpanel));

			// Handle keyboard events
			tabpanel.trigger.addEventListener('keydown', this._tabKeydownEvent.bind(this, tabpanel));

			// Insert the created elements
			tabpanel.tab.appendChild(tabpanel.trigger);
			this.tablist.appendChild(tabpanel.tab);

			// Set up the tabpanel
			tabpanel.tabpanelElement.setAttribute('role', 'tabpanel');
			tabpanel.tabpanelElement.setAttribute('tabindex', '-1');
			tabpanel.tabpanelElement.setAttribute('aria-labelledby', tabpanel.trigger.id);
			let titleElement = tabpanelElement.querySelector(this.settings.tabpanelTitleSelector);
			if (titleElement && this.settings.hideTitle) {
				titleElement.remove();
			}

			this.tabpanels.push(tabpanel);
		});

		// Remove the link list if there is one and add the created tablist to the DOM
		if (this.linkList) {
			this.linkList.remove();
		}
		this.element.insertBefore(this.tablist, this.element.firstChild);

		/* Give the enabled tabs element a class to
		 * 1. Allow styling depending on whether it is enabled or not (JS off, script errors, whatever)
		 * 2. Check if this element already has tabs enabled
		 */
		this.element.classList.add(this.settings.enabledTabsClass);

		if (tabPanelToDisplay) {
			// Display a tabpanel but don't set focus to the active tab, e.g. when the page loads
			this.displayTabpanel(tabPanelToDisplay.id, false);
		}

		// Manage tab history
		window.addEventListener('popstate', this._historyChange.bind(this));
	}

	/*
	 * Handle Tab click event
	 */
	_tabClickEvent (tabpanel) {
		this.displayTabpanel(tabpanel.id, true);
		this.setHistory(tabpanel.id);
	}

	/*
	 * Handle Tab keyboard events
	 */
	_tabKeydownEvent (tabpanel, e) {
		const tabTrigger = e.target;
		const previous = tabTrigger.parentNode.previousElementSibling || this.tablist.lastChild;
		const next = tabTrigger.parentNode.nextElementSibling || this.tablist.firstChild;
		switch (e.which) {
			case keycodes.LEFT:
				// Move to the previous or last tab
				previous.querySelector('.' + this.settings.tabListTriggerClass).click();
				break;
			case keycodes.RIGHT:
				// Move to the next or first tab
				next.querySelector('.' + this.settings.tabListTriggerClass).click();
				break;
			case keycodes.HOME:
				e.preventDefault();
				// Move to the first tab
				this._getFirstTab().click();
				break;
			case keycodes.END:
				e.preventDefault();
				// Move to the last tab
				this._getLastTab().click();
				break;
			case keycodes.DOWN:
				// Move focus to the displayed tabpanel
				e.preventDefault();
				tabpanel.tabpanelElement.focus();
				break;
		}
	}

	_getFirstTab () {
		return this.tablist.firstChild.querySelector('.' + this.settings.tabListTriggerClass);
	}

	_getLastTab () {
		return this.tablist.lastChild.querySelector('.' + this.settings.tabListTriggerClass);
	}

	_historyChange () {
		if (document.location.hash) {
			let tabId = document.location.hash.replace('#', '');

			if (document.querySelector(`#${tabId}`)) {
				this.displayTabpanel(tabId, true);
			}
		}
		else if (this.tabpanelElements) {
			this.displayTabpanel(this.tabpanelElements[0].id, true);
		}
	}

	/* =============================================================================
	 * Public methods
	============================================================================= */

	/**
	 * Display a tabpanel and hide all others
	 */
	displayTabpanel (tabId, setFocus = false) {
		this.tabpanels.forEach(tabpanel => {
			if (tabId === tabpanel.id) {
				tabpanel.trigger.setAttribute('aria-selected', 'true');
				tabpanel.trigger.setAttribute('tabindex', '0');
				if (setFocus) {
					tabpanel.trigger.focus();
				}
				tabpanel.tabpanelElement.removeAttribute('hidden');
			}
			else {
				tabpanel.trigger.setAttribute('aria-selected', 'false');
				tabpanel.trigger.setAttribute('tabindex', '-1');
				tabpanel.tabpanelElement.setAttribute('hidden', '');
			}
		});
	}

	setHistory (tabId) {
		history.pushState(null, null, `#${tabId}`);
	}
}

document.addEventListener('newcontent', (e) => {
	e.target.querySelectorAll('.c-tabs:not(.c-tabs--static)').forEach((element) => {
		element.tabs = new Tabs({
			debug: false,
			element: element
		});
	});
});

export default Tabs;



// WEBPACK FOOTER //
// ./components/tabs/c-tabs.js